// 261010 Epic 2 / P3c — CHÍNH SÁCH hồ sơ phong cách cấp truyện (thuần: không antd/router, jest chạy được).
// Quyết: dải theo trạng thái · nút nào bật · kiểm nháp / duyệt (CÙNG luật và CÙNG `path` với BE `filmStyleProfileDomain` — FE kiểm trước, BE là chuẩn) · dựng body PUT · gom lỗi theo khối · lỗi API ⇒ chữ.
// Hồ sơ là NGUỒN luật; Phase này chưa bước nào đọc luật (Phase doc §Phạm vi) nên mọi câu nhắc đều nói "chưa ảnh hưởng bước nào" / "chưa thành cũ". KHÔNG hiện giá (AI chữ).
import type { FilmFailedJob, FilmRunningJob } from '../../types/film';
import type {
  BandKey, FilmStyleProfile, RhythmMode, StyleIssue, StyleLook, StyleOwner, StyleProfileContent, StyleProfileSaveBody, StyleProfileStatus, StyleProfileView, StyleRhythm, StyleRule,
} from '../../types/filmStyleProfile';
import { workflowActionError, type ActionError } from './workflowActionPolicy';

export type StyleBlockKey = 'audience' | 'rules' | 'rhythm' | 'look';
export const STYLE_BLOCKS: StyleBlockKey[] = ['audience', 'rules', 'rhythm', 'look'];
export const BLOCK_SAVE_LABEL: Record<StyleBlockKey, string> = { audience: 'Lưu trải nghiệm', rules: 'Lưu luật', rhythm: 'Lưu nhịp', look: 'Lưu màu & ánh sáng' };
export const BLOCK_NAME: Record<StyleBlockKey, string> = { audience: 'Trải nghiệm khán giả', rules: 'Luật phong cách', rhythm: 'Nhịp short drama', look: 'Màu & ánh sáng' };

export const OWNER_ORDER: StyleOwner[] = ['DIRECTING', 'ACTING', 'CINEMATOGRAPHY', 'EDITING'];
export const OWNER_LABEL: Record<StyleOwner, string> = { DIRECTING: 'Đạo diễn', ACTING: 'Diễn xuất', CINEMATOGRAPHY: 'Quay phim', EDITING: 'Dựng' };
/** Cùng giới hạn BE (`STYLE_LIMITS`). */
export const STYLE_LIMITS = { maxRules: 8, minRulesToApprove: 5, maxText: 600, maxBand: 10 } as const;
export const RHYTHM_MODE_LABEL: Record<RhythmMode, string> = { DENSE: 'Dày (dense)', BALANCED: 'Cân bằng', SUSTAINED: 'Dài hơi' };
export const BAND_ROWS: Array<{ key: BandKey; label: string }> = [
  { key: 'air', label: 'không khí / cảm xúc' }, { key: 'dialogue', label: 'đối thoại / đối đầu' }, { key: 'action', label: 'hành động / cao trào' },
];

/** Câu ghi chú của khối Nhịp — NGUYÊN VĂN mẫu P1 (quality-gap-plan §5: nhịp là tiêu chí, không phải mục tiêu cắt nhanh). */
export const RHYTHM_NOTE = 'Đây là con số khởi điểm để thiết kế và thử, không phải chuẩn ngành hay cam kết giữ chân người xem. Nhịp nhanh không có nghĩa là nói nhanh hay rút thoại.';
export const MAX_RULES_TEXT = 'Tối đa 8 luật: ít luật mới làm được, nhiều luật thì tranh nhau.';
export const REPLACE_CONFIRM_TITLE = 'Thay nội dung đang có bằng bản AI mới?';
export const REPLACE_CONFIRM_BODY = 'Bản đã duyệt vẫn giữ để so. Lượt này chỉ dùng AI chữ.';
export const DRAFTING_TEXT = 'AI đang đề xuất hồ sơ phong cách…';
const NOT_LOADED_TEXT = 'Chưa tải được hồ sơ phong cách — bấm Tải lại.';

export interface StyleAction { key: string; label: string; visible: boolean; enabled: boolean; reason?: string; confirm: 'NONE' | 'REPLACE' }
export interface StyleStrip { state: 'LOADING' | 'NONE' | 'DRAFT' | 'APPROVED' | 'DRAFTING'; text: string; chip: { label: string; tone: 'ok' | 'warn' | 'doing' } | null; drift: string | null }

const trimStr = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export const lookEmpty = (l: StyleLook | null): boolean => !l || !(trimStr(l.genreTone) || trimStr(l.palette) || trimStr(l.lighting) || trimStr(l.filmLook));

/** Hồ sơ ⇒ đúng 4 khối nội dung (bản sao nông); chưa có hồ sơ ⇒ rỗng. */
export const contentOf = (p: FilmStyleProfile | null): StyleProfileContent => (p
  ? { audience: { ...p.audience }, rules: p.rules.map((r) => ({ ...r, owners: [...r.owners] })), rhythm: p.rhythm ? { ...p.rhythm, bands: { air: { ...p.rhythm.bands.air }, dialogue: { ...p.rhythm.bands.dialogue }, action: { ...p.rhythm.bands.action } } } : null, look: p.look ? { ...p.look } : null }
  : { audience: { feel: '', know: '', realism: '' }, rules: [], rhythm: null, look: null });

// ───────────────────────── dải ─────────────────────────

/** Plan đang theo bản KHÁC bản đã duyệt hiện tại (so `hash`). Plan cũ không có `planRef` (null) ⇒ KHÔNG nói gì (mọi plan trước P3c đều null — câu đó sẽ thành nhiễu). */
export function driftTextOf(v: StyleProfileView | null): string | null {
  const approved = v?.profile?.approved;
  if (!v || v.status === 'NONE' || !approved || !v.planRef) return null;
  return v.planRef.hash !== approved.hash ? `tập này theo bản v${v.planRef.version}` : null;
}

/** `storyName` tham số (trang truyền xuống) thắng; không có thì lấy `view.storyName` do BE trả (lượt 2). */
export function stripOf(v: StyleProfileView | null, storyName?: string | null): StyleStrip {
  if (!v) return { state: 'LOADING', text: 'Đang tải hồ sơ phong cách…', chip: null, drift: null };
  if (v.draftJob) return { state: 'DRAFTING', text: DRAFTING_TEXT, chip: { label: 'đang chạy', tone: 'doing' }, drift: null };
  const name = trimStr(storyName) || trimStr(v.storyName);
  const of = name ? `của truyện “${name}”` : 'của truyện này';
  if (v.status === 'NONE') return { state: 'NONE', text: 'Truyện chưa có hồ sơ phong cách', chip: null, drift: null };
  if (v.status === 'APPROVED') {
    return { state: 'APPROVED', text: `Đang dùng phong cách ${of} · bản v${v.profile?.approved?.version ?? '?'}`, chip: { label: 'đã duyệt', tone: 'ok' }, drift: driftTextOf(v) };
  }
  return { state: 'DRAFT', text: `AI đề xuất hồ sơ phong cách ${of}`, chip: { label: 'chờ bạn duyệt', tone: 'warn' }, drift: driftTextOf(v) };
}

/** UI-assets — tiêu đề CỐ ĐỊNH của thẻ hồ sơ phong cách ở cuối bước Tài sản (câu dài `strip.text` chuyển thành tooltip). Đang tải / đang soạn thì giữ câu của dải (người dùng cần biết máy đang làm gì). */
export const STYLE_CARD_TITLE = 'Hồ sơ phong cách (AI đề xuất)';
export function styleCardTitleOf(strip: Pick<StyleStrip, 'state' | 'text'>): string {
  return strip.state === 'LOADING' || strip.state === 'DRAFTING' ? strip.text : STYLE_CARD_TITLE;
}

/** Dòng phụ khối Màu & ánh sáng (QĐ 5): `look` chỉ áp cho plan TẠO MỚI; plan không theo đúng bản đang dùng giữ màu riêng. */
export function lookNoteOf(v: StyleProfileView | null): string | null {
  if (!v || !v.profile) return null;
  const approved = v.profile.approved;
  const follows = !!v.planRef && !!approved && v.planRef.hash === approved.hash;
  return follows ? null : 'Tập này dùng màu & ánh sáng riêng (tạo trước bản hồ sơ này) — sửa hồ sơ phong cách ở đầu bước 2 nếu cần';
}

/** Câu nhắc cạnh nút lưu của từng khối. `label` = "luật" / "nhịp" / … */
export function saveNote(status: StyleProfileStatus, label: string): string {
  return status === 'APPROVED'
    ? `Lưu ${label} đã duyệt: hồ sơ về “chờ duyệt”. Các bước sau chưa đọc hồ sơ ở bản này nên chưa thành “cũ”.`
    : 'Hồ sơ chưa duyệt nên chưa ảnh hưởng bước nào.';
}

// ───────────────────────── nút ─────────────────────────

export interface StyleCtx { busy: boolean; runActive: boolean; dirty: StyleBlockKey[] }

/** Máy tự chạy (FILM_WORKFLOW_RUN) đang chạy trên plan ⇒ middleware BE che mọi PUT / POST (`WORKFLOW_RUN_ACTIVE`). Job đề xuất KHÔNG vào danh sách việc ghi plan. */
export const isRunActive = (jobs: Array<Pick<FilmRunningJob, 'type'>> | undefined): boolean => !!jobs?.some((j) => j.type === 'FILM_WORKFLOW_RUN');

/** Cổng chung cho MỌI nút ghi (lưu từng khối, thêm / sửa / xoá luật, đề xuất, duyệt). */
export function writeGate(v: StyleProfileView | null, o: { busy: boolean; runActive: boolean }): { ok: boolean; reason?: string } {
  if (!v) return { ok: false, reason: NOT_LOADED_TEXT };
  if (v.draftJob) return { ok: false, reason: 'AI đang đề xuất hồ sơ phong cách — đợi xong rồi thao tác, đừng bấm lại.' };
  if (o.runActive) return { ok: false, reason: 'Đang có máy tự chạy chặng chạy trên plan này — đợi xong rồi thao tác.' };
  if (o.busy) return { ok: false, reason: 'Đang gửi yêu cầu — đợi một chút.' };
  return { ok: true };
}

export function styleActions(v: StyleProfileView | null, ctx: StyleCtx): StyleAction[] {
  const gate = writeGate(v, ctx);
  const hasProfile = !!v?.profile;
  const dirtyNames = ctx.dirty.map((k) => `“${BLOCK_NAME[k]}”`).join(', ');
  const approveOk = gate.ok && ctx.dirty.length === 0;
  return [
    { key: 'STYLE_GENERATE', label: hasProfile ? 'AI đề xuất lại' : 'AI đề xuất', visible: true, enabled: gate.ok, confirm: hasProfile ? 'REPLACE' : 'NONE', ...(gate.ok ? {} : { reason: gate.reason }) },
    {
      key: 'STYLE_APPROVE', label: 'Duyệt hồ sơ phong cách', visible: v?.status === 'DRAFT', enabled: approveOk, confirm: 'NONE',
      ...(approveOk ? {} : { reason: !gate.ok ? gate.reason : `Có chỉnh sửa chưa lưu ở ${dirtyNames} — lưu rồi hãy duyệt (duyệt chỉ áp bản đã lưu).` }),
    },
  ];
}

// ───────────────────────── luật ─────────────────────────

/** Số R lớn nhất + 1 — KHÔNG dùng `length + 1` (sau khi xoá R2 của R1..R3 sẽ trùng R3). */
export function nextRuleId(rules: Array<Pick<StyleRule, 'id'>>): string {
  const max = rules.reduce((m, r) => { const n = /^R(\d+)$/.exec(r.id); return n ? Math.max(m, Number(n[1])) : m; }, 0);
  return `R${max + 1}`;
}
/**
 * Chỗ HIỂN THỊ ghép "Khi … , thì …" tự thêm chữ "Khi" / "thì" ⇒ AI viết `when` / `then` bắt đầu bằng đúng chữ đó sẽ hiện "Khi Khi …". Bỏ tiền tố trùng (hoa / thường, có hoặc không dấu phẩy / hai chấm).
 * CHỈ để hiển thị: dữ liệu lưu và nội dung trình sửa giữ nguyên. "khiến", "thìa"… không phải tiền tố (sau chữ phải là khoảng trắng / dấu câu / hết chuỗi). Bỏ xong mà rỗng ⇒ giữ nguyên.
 */
const stripLead = (text: string, word: 'khi' | 'thì'): string => {
  const t = text.trim();
  const m = new RegExp(`^${word}(?=[\\s,:;]|$)[\\s,:;]*`, 'i').exec(t);
  const rest = m ? t.slice(m[0].length).trim() : t;
  return stripTail(rest || t);
};
/**
 * Dấu câu CUỐI (`.` `。` `;` `,` và khoảng trắng, lặp bao nhiêu cũng bỏ): dữ liệu AI hay kết bằng "." nên ghép "Khi <when>, thì <then>." ra "đỏ., thì" và "..".
 * Chỉ để hiển thị — chỗ ghép tự thêm đúng MỘT dấu chấm cuối. Chữ chỉ toàn dấu câu ⇒ rỗng.
 */
const stripTail = (s: string): string => s.replace(/[\s.。;,]+$/u, '');
export const ruleWhenText = (when: string): string => stripLead(when, 'khi');
export const ruleThenText = (then: string): string => stripLead(then, 'thì');
/** Câu luật hiển thị: "Khi <when>, thì <then>." — mỗi vế bỏ tiền tố trùng + dấu câu cuối, rồi đúng MỘT dấu chấm cuối. Panel vẽ cùng hai hàm trên (in đậm "Khi" / "thì"). */
export const ruleSentence = (r: Pick<StyleRule, 'when' | 'then'>): string => `Khi ${ruleWhenText(r.when)}, thì ${ruleThenText(r.then)}.`;

/** `baseRevision` cho lần ghi kế tiếp = revision của hồ sơ trong view MỚI NHẤT (response của GET / PUT / approve — approve cũng tăng revision). Chưa có hồ sơ ⇒ 0. */
export const baseRevisionOf = (v: StyleProfileView | null): number => v?.profile?.revision ?? 0;

export const canAddRule =(rules: unknown[]): boolean => rules.length < STYLE_LIMITS.maxRules;
export const withRule = (rules: StyleRule[], rule: StyleRule): StyleRule[] => (rules.some((r) => r.id === rule.id) ? rules.map((r) => (r.id === rule.id ? rule : r)) : [...rules, rule]);
export const withoutRule = (rules: StyleRule[], id: string): StyleRule[] => rules.filter((r) => r.id !== id);

const ownersOf = (v: StyleOwner[]): StyleOwner[] => OWNER_ORDER.filter((o) => v.includes(o));
export interface RuleForm { id: string; title: string; when: string; then: string; exception: string; check: string; owners: StyleOwner[] }
export const ruleToForm = (r: StyleRule): RuleForm => ({ id: r.id, title: r.title, when: r.when, then: r.then, exception: r.exception ?? '', check: r.check, owners: [...r.owners] });
export const ruleFromForm = (f: RuleForm): StyleRule => ({
  id: f.id, title: f.title.trim(), when: f.when.trim(), then: f.then.trim(), exception: f.exception.trim() || null, check: f.check.trim(), owners: ownersOf(f.owners),
});
export const emptyRuleForm = (rules: Array<Pick<StyleRule, 'id'>>): RuleForm => ({ id: nextRuleId(rules), title: '', when: '', then: '', exception: '', check: '', owners: [] });

// ───────────────────────── nhịp ─────────────────────────

export interface RhythmForm { mode: RhythmMode; bands: Record<BandKey, { lo: string; hi: string }>; exceptions: string }
const blankBand = (): { lo: string; hi: string } => ({ lo: '', hi: '' });
/** Chưa có nhịp ⇒ ô số TRỐNG (không lấy số `BANDS` của mẫu làm mặc định: đó là số minh hoạ cho Fredo, không phải khuyến nghị). */
export const rhythmToForm = (r: StyleRhythm | null): RhythmForm => (r
  ? { mode: r.mode, bands: { air: { lo: String(r.bands.air.lo), hi: String(r.bands.air.hi) }, dialogue: { lo: String(r.bands.dialogue.lo), hi: String(r.bands.dialogue.hi) }, action: { lo: String(r.bands.action.lo), hi: String(r.bands.action.hi) } }, exceptions: r.exceptions }
  : { mode: 'BALANCED', bands: { air: blankBand(), dialogue: blankBand(), action: blankBand() }, exceptions: '' });
const numOf = (s: string): number => { const t = s.trim().replace(',', '.'); return t === '' ? NaN : Number(t); };
export const rhythmFromForm = (f: RhythmForm): StyleRhythm => ({
  mode: f.mode,
  bands: { air: { lo: numOf(f.bands.air.lo), hi: numOf(f.bands.air.hi) }, dialogue: { lo: numOf(f.bands.dialogue.lo), hi: numOf(f.bands.dialogue.hi) }, action: { lo: numOf(f.bands.action.lo), hi: numOf(f.bands.action.hi) } },
  exceptions: f.exceptions.trim(),
});

// ───────────────────────── body PUT ─────────────────────────

export type BlockValue = StyleProfileContent[StyleBlockKey];
/**
 * Mỗi nút lưu PUT TOÀN hồ sơ: khối đang lưu lấy từ `value`, các khối còn lại lấy từ bản ĐÃ LƯU ở server (nháp chưa lưu của khối khác KHÔNG bị kéo theo).
 * `baseRevision` = revision lúc mở (chưa có hồ sơ ⇒ 0). Khối Màu bốn ô trống mà hồ sơ chưa có `look` ⇒ null (lưu không đổi gì thì hash không nhảy).
 */
export function putBody(profile: FilmStyleProfile | null, block: StyleBlockKey, value: BlockValue): StyleProfileSaveBody {
  const base = contentOf(profile);
  const next = { ...base, [block]: value } as StyleProfileContent;
  if (block === 'look' && lookEmpty(next.look) && !profile?.look) next.look = null;
  return { baseRevision: profile?.revision ?? 0, audience: next.audience, rules: next.rules, rhythm: next.rhythm, look: next.look };
}

// ───────────────────────── kiểm (mirror BE) ─────────────────────────

const RULE_ID = /^R\d+$/;
const tooLong = (out: StyleIssue[], path: string, v: string | null | undefined): void => {
  const n = (v ?? '').trim().length;
  if (n > STYLE_LIMITS.maxText) out.push({ path, message: `tối đa ${STYLE_LIMITS.maxText} ký tự (đang ${n})` });
};
const bandValid = (b: { lo: number; hi: number }): boolean => Number.isFinite(b.lo) && Number.isFinite(b.hi) && b.lo > 0 && b.lo <= b.hi && b.hi <= STYLE_LIMITS.maxBand;

/** Kiểm khi lưu nháp (PUT) — cùng `validateDraft` của BE: cho phép chưa đủ (0 luật, trường rỗng, chưa chọn vai). */
export function draftIssues(c: StyleProfileContent): StyleIssue[] {
  const out: StyleIssue[] = [];
  for (const k of ['feel', 'know', 'realism'] as const) tooLong(out, `audience.${k}`, c.audience[k]);
  if (c.rules.length > STYLE_LIMITS.maxRules) out.push({ path: 'rules', message: `tối đa ${STYLE_LIMITS.maxRules} luật (đang ${c.rules.length})` });
  const seen = new Set<string>();
  c.rules.forEach((r, i) => {
    const p = `rules[${i}]`;
    if (!RULE_ID.test(r.id)) out.push({ path: `${p}.id`, message: 'mã luật phải dạng R1, R2…' });
    else if (seen.has(r.id)) out.push({ path: `${p}.id`, message: `mã luật ${r.id} bị trùng` });
    else seen.add(r.id);
    for (const k of ['title', 'when', 'then', 'check'] as const) tooLong(out, `${p}.${k}`, r[k]);
    tooLong(out, `${p}.exception`, r.exception);
  });
  if (c.rhythm) {
    for (const row of BAND_ROWS) if (!bandValid(c.rhythm.bands[row.key])) out.push({ path: `rhythm.bands.${row.key}`, message: `cần 0 < lo ≤ hi ≤ ${STYLE_LIMITS.maxBand} (shot / 10 giây)` });
    tooLong(out, 'rhythm.exceptions', c.rhythm.exceptions);
  }
  if (c.look) for (const k of ['genreTone', 'palette', 'lighting', 'filmLook'] as const) tooLong(out, `look.${k}`, c.look[k]);
  return out;
}

/** Kiểm khi duyệt — cùng `validateForApprove` của BE: 5–8 luật; mỗi luật đủ title / when / then / check + ≥1 vai; `audience.feel` không rỗng. */
export function approveIssues(c: StyleProfileContent): StyleIssue[] {
  const out: StyleIssue[] = [];
  const n = c.rules.length;
  if (n < STYLE_LIMITS.minRulesToApprove || n > STYLE_LIMITS.maxRules) out.push({ path: 'rules', message: `cần ${STYLE_LIMITS.minRulesToApprove}–${STYLE_LIMITS.maxRules} luật để duyệt (đang có ${n})` });
  const label = { title: 'tên luật', when: '“khi”', then: '“thì”', check: 'tiêu chí kiểm' } as const;
  c.rules.forEach((r, i) => {
    for (const k of ['title', 'when', 'then', 'check'] as const) if (!trimStr(r[k])) out.push({ path: `rules[${i}].${k}`, message: `${label[k]} không được để trống` });
    if (!r.owners || r.owners.length < 1) out.push({ path: `rules[${i}].owners`, message: 'cần ít nhất một vai phụ trách' });
  });
  if (!trimStr(c.audience.feel)) out.push({ path: 'audience.feel', message: '“khán giả cần cảm thấy gì” không được để trống' });
  return out;
}

// ───────────────────────── lỗi theo khối ─────────────────────────

export function blockOfPath(path: string): StyleBlockKey | 'GENERAL' {
  const head = /^[a-zA-Z]+/.exec(path)?.[0];
  return head === 'audience' || head === 'rules' || head === 'rhythm' || head === 'look' ? head : 'GENERAL';
}
export function issuesByBlock(issues: StyleIssue[]): Record<StyleBlockKey | 'GENERAL', StyleIssue[]> {
  const out: Record<StyleBlockKey | 'GENERAL', StyleIssue[]> = { audience: [], rules: [], rhythm: [], look: [], GENERAL: [] };
  for (const i of issues) out[blockOfPath(i.path)].push(i);
  return out;
}

const FIELD_LABEL: Record<string, string> = {
  title: 'Tên luật', when: 'Khi', then: 'Thì', exception: 'Ngoại lệ', check: 'Tiêu chí kiểm', owners: 'Vai phụ trách', id: 'Mã luật',
  feel: 'Khán giả cần cảm thấy gì', know: 'Khán giả biết gì, khi nào', realism: 'Mức hiện thực / cường điệu',
  genreTone: 'Genre / tone', palette: 'Bảng màu', lighting: 'Ánh sáng', filmLook: 'Film look', exceptions: 'Ngoại lệ có căn cứ',
};
const withField = (label: string, message: string): string => (message.toLowerCase().includes(label.toLowerCase()) ? message : `${label}: ${message}`);

/**
 * Một lỗi `{ path, message }` ⇒ một dòng chữ dưới khối. Chỉ số `rules[i]` tính trên MẢNG ĐÃ GỬI (`sent`) và đổi ra mã luật (R4, không phải thứ tự).
 * Câu BE đã tự nêu tên trường ("tiêu chí kiểm không được để trống") thì không lặp lại tên trường.
 */
export function issueLine(issue: StyleIssue, sent: Array<Pick<StyleRule, 'id'>>): string {
  const rule = /^rules\[(\d+)\]\.(\w+)$/.exec(issue.path);
  if (rule) {
    const idx = Number(rule[1]);
    const who = sent[idx] ? `Luật ${sent[idx].id}` : `Luật số ${idx + 1}`;
    const field = FIELD_LABEL[rule[2]];
    return `${who} — ${field ? withField(field, issue.message) : issue.message}`;
  }
  const band = /^rhythm\.bands\.(\w+)$/.exec(issue.path);
  if (band) {
    const row = BAND_ROWS.find((b) => b.key === band[1]);
    return row ? `Nhịp (${row.label}): ${issue.message}` : issue.message;
  }
  const flat = /^(audience|look|rhythm)\.(\w+)$/.exec(issue.path);
  if (flat && FIELD_LABEL[flat[2]]) return withField(FIELD_LABEL[flat[2]], issue.message);
  return issue.message;
}

// ───────────────────────── lỗi API ⇒ chữ ─────────────────────────

/** `kind` của `workflowActionError` (khi rơi về nó) hoặc các loại riêng của hồ sơ. `reload`: có nên nạp lại hồ sơ sau lỗi này (lỗi kiểm tra nhập liệu thì không — giữ nguyên chữ đang gõ). */
export interface StyleActionError { kind: ActionError['kind'] | 'INVALID' | 'TOO_LARGE' | 'TOKEN_COUNT' | 'AI'; text: string; issues: StyleIssue[]; reload: boolean }

export const STYLE_CHANGED_ELSEWHERE_TEXT = 'Hồ sơ phong cách vừa đổi ở nơi khác — tải lại rồi sửa lại.';
const DRAFT_JOB_TYPE = 'FILM_STYLE_PROFILE_DRAFT';

/**
 * Lỗi của hồ sơ phong cách. Đứng TRƯỚC `workflowActionError` vì hai chỗ nó nói sai: `CHANGED_ELSEWHERE` in "Video này vừa đổi…" và `JOB_RUNNING` của job đề xuất in tên loại việc thô.
 * Mã khác (máy tự chạy, không phải Sky, mã lạ…) rơi về `workflowActionError`.
 */
export function styleProfileActionError(err: any, what: string): StyleActionError {
  const d = err?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : '';
  const mk = (kind: StyleActionError['kind'], text: string, reload = true, issues: StyleIssue[] = []): StyleActionError => ({ kind, text: `${what}: ${text}`, issues, reload });
  if (code === 'STYLE_PROFILE_INVALID') {
    const issues = Array.isArray(d?.issues) ? (d.issues as StyleIssue[]).filter((i) => i && typeof i.path === 'string' && typeof i.message === 'string') : [];
    return mk('INVALID', 'hồ sơ chưa hợp lệ — xem các mục được nêu bên dưới từng khối.', false, issues);
  }
  if (code === 'STYLE_PROFILE_INPUT_TOO_LARGE') return mk('TOO_LARGE', 'đầu vào quá dài nên chưa gọi AI — rút bớt video hoặc bối cảnh của plan rồi thử lại.');
  if (code === 'STYLE_PROFILE_TOKEN_COUNT_FAILED') return mk('TOKEN_COUNT', 'không đếm được độ dài đầu vào nên chưa gọi AI — thử lại sau ít phút.');
  if (code === 'STYLE_PROFILE_AI_FAILED' || code === 'STYLE_PROFILE_AI_INVALID') return mk('AI', 'AI chưa trả được hồ sơ dùng được — bấm đề xuất lại.');
  if (code === 'CHANGED_ELSEWHERE') return { kind: 'CHANGED_ELSEWHERE', text: STYLE_CHANGED_ELSEWHERE_TEXT, issues: [], reload: true };
  if (code === 'JOB_RUNNING' && d?.jobType === DRAFT_JOB_TYPE) return mk('BUSY', 'AI đang đề xuất hồ sơ phong cách của truyện này — đợi xong rồi bấm Tải lại, đừng bấm lại.');
  const base = workflowActionError(err, what);
  return { kind: base.kind, text: base.text, issues: [], reload: true };
}

/** Job đề xuất chạy nền nên lỗi KHÔNG đi theo response 202: đọc `failures[id = jobId].note` của `GET /plans/:id/jobs` (note là câu tiếng Việt đã làm sạch của BE). */
export function draftFailureNote(failures: FilmFailedJob[] | undefined, jobId: string | null | undefined): string | null {
  if (!jobId) return null;
  const f = failures?.find((x) => x.id === jobId);
  return f ? `Lượt AI đề xuất không thành: ${f.note}` : null;
}
